/**
 * Vektor-Platzplan (SVG-Editor): Typen, Geometrie-Helfer, SVG-Import und -Export.
 *
 * Koordinaten im Dokument sind Plan-Einheiten (Pixel des Quellbilds bzw.
 * SVG-viewBox). Stellplatz-Polygone (`pitches.map_polygon`) sind dagegen auf
 * 0–1 normalisiert – die Umrechnung übernimmt die API beim Speichern.
 *
 * Spiegelt api/schemas/site_plan_document.py.
 */

import type { MapPoint } from "@/lib/sitePlan";
import { polygonCentroid } from "@/lib/sitePlan";
import {
  getSymbolDefinition,
  serializeSymbolDefs,
  SYMBOL_BOX,
  symbolDomId,
} from "@/lib/sitePlanSymbols";
import type { Pitch } from "@/types/camping";

export type PlanLayer = "pitch" | "road" | "building" | "water" | "green" | "parking" | "other";

export interface PlanPoint {
  x: number;
  y: number;
}

export interface PlanShape {
  id: string;
  kind: "polygon";
  points: PlanPoint[];
  label: string | null;
  layer: PlanLayer;
  fill: string | null;
  stroke: string | null;
  stroke_width: number | null;
  opacity: number | null;
  pitch_id: string | null;
  locked: boolean;
  /** false blendet die Nummer auf der Fläche aus. Fehlend gilt als angezeigt. */
  show_label?: boolean;
  /** false behält die eigene Füllfarbe statt der Farbe der Platzkategorie. */
  use_category_color?: boolean;
}

export interface PlanSymbol {
  id: string;
  type: string;
  x: number;
  y: number;
  size: number;
  rotation: number;
  label: string | null;
  color: string | null;
}

export interface PlanText {
  id: string;
  x: number;
  y: number;
  text: string;
  size: number;
  rotation: number;
  color: string | null;
}

/** Offene Linie: Anfahrt zu einem Stellplatz. Mit Platzbezug bleibt sie im Plan ausgeblendet. */
export interface PlanRoute {
  id: string;
  points: PlanPoint[];
  pitch_id: string | null;
  color: string | null;
  stroke_width: number | null;
}

export interface SitePlanVectorDocument {
  version: 1;
  width: number;
  height: number;
  shapes: PlanShape[];
  symbols: PlanSymbol[];
  texts: PlanText[];
  routes: PlanRoute[];
  background_color: string | null;
}

export interface SitePlanDocumentOut {
  document: SitePlanVectorDocument | null;
  has_source_image: boolean;
  source_image_data: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export interface SitePlanDocumentSaveResult extends SitePlanDocumentOut {
  synced_pitches: number;
  cleared_pitches: number;
  background_published: boolean;
}

export interface SitePlanDocumentUpdate {
  document: SitePlanVectorDocument;
  sync_pitches?: boolean;
  clear_unassigned_pitches?: boolean;
  background_svg?: string | null;
  source_image_data?: string | null;
  clear_source_image?: boolean;
}

export interface SitePlanAiStatus {
  available: boolean;
  provider: string | null;
  model: string | null;
  reason: string | null;
}

export interface SitePlanVectorizeResult {
  document: SitePlanVectorDocument;
  warnings: string[];
  provider: string;
  model: string;
  matched_pitches: number;
  unmatched_labels: string[];
}

export const PLAN_LAYERS: { key: PlanLayer; label: string; fill: string }[] = [
  { key: "pitch", label: "Stellplatz", fill: "#f5b400" },
  { key: "road", label: "Weg / Straße", fill: "#b8b8b8" },
  { key: "building", label: "Gebäude", fill: "#8e8e93" },
  { key: "water", label: "Wasser", fill: "#2f6fe4" },
  { key: "green", label: "Grünfläche", fill: "#5bb04d" },
  { key: "parking", label: "Parkplatz", fill: "#c7c7cc" },
  { key: "other", label: "Sonstiges", fill: "#d1d1d6" },
];

const LAYER_BY_KEY = new Map(PLAN_LAYERS.map((l) => [l.key, l]));

export function layerLabel(layer: PlanLayer): string {
  return LAYER_BY_KEY.get(layer)?.label ?? layer;
}

export function layerDefaultFill(layer: PlanLayer): string {
  return LAYER_BY_KEY.get(layer)?.fill ?? "#d1d1d6";
}

/** Schnell-Paletten für Platzflächen (Farben der typischen Platzplan-Legende). */
export const PITCH_FILL_PRESETS: { label: string; fill: string }[] = [
  { label: "Standard", fill: "#f5b400" },
  { label: "Komfort", fill: "#e0432a" },
  { label: "Komfort Wasserblick", fill: "#1ea0e6" },
  { label: "Dauerstellplatz", fill: "#9ccf6a" },
  { label: "Grau", fill: "#9ca3af" },
  { label: "Violett", fill: "#8b5cf6" },
];

export const MAX_PLAN_SHAPES = 2000;

export function newElementId(prefix: string): string {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "").slice(0, 10)
      : Math.random().toString(36).slice(2, 12);
  return `${prefix}-${random}`;
}

export function emptyDocument(width: number, height: number): SitePlanVectorDocument {
  return {
    version: 1,
    width,
    height,
    shapes: [],
    symbols: [],
    texts: [],
    routes: [],
    background_color: null,
  };
}

export function createShape(
  points: PlanPoint[],
  options: Partial<Omit<PlanShape, "id" | "kind" | "points">> = {},
): PlanShape {
  const layer = options.layer ?? "pitch";
  return {
    id: newElementId("a"),
    kind: "polygon",
    points,
    label: options.label ?? null,
    layer,
    fill: options.fill ?? layerDefaultFill(layer),
    stroke: options.stroke ?? null,
    stroke_width: options.stroke_width ?? null,
    opacity: options.opacity ?? null,
    pitch_id: options.pitch_id ?? null,
    locked: options.locked ?? false,
  };
}

export function createSymbol(type: string, x: number, y: number, size: number): PlanSymbol {
  return {
    id: newElementId("s"),
    type,
    x,
    y,
    size,
    rotation: 0,
    label: null,
    color: null,
  };
}

export function createText(text: string, x: number, y: number, size: number): PlanText {
  return { id: newElementId("t"), x, y, text, size, rotation: 0, color: null };
}

export const ROUTE_COLOR = "#0a84ff";

export function createRoute(
  points: PlanPoint[],
  options: Partial<Omit<PlanRoute, "id" | "points">> = {},
): PlanRoute {
  return {
    id: newElementId("r"),
    points,
    pitch_id: options.pitch_id ?? null,
    color: options.color ?? null,
    stroke_width: options.stroke_width ?? null,
  };
}

/** Ältere Dokumente ohne das Feld bleiben gültig. */
export function ensureRoutes(doc: SitePlanVectorDocument): SitePlanVectorDocument {
  return doc.routes ? doc : { ...doc, routes: [] };
}

/** Zugeordnete Anfahrten sind unsichtbar, bis ihr Platz eingeblendet wird. */
export function isRouteVisible(route: PlanRoute, revealedPitchId: string | null): boolean {
  return !route.pitch_id || route.pitch_id === revealedPitchId;
}

/** Bestehende Stellplatz-Polygone (normalisiert) als Dokument übernehmen. */
export function documentFromPitchPolygons(
  pitches: Pitch[],
  width: number,
  height: number,
): SitePlanVectorDocument {
  const doc = emptyDocument(width, height);
  for (const pitch of pitches) {
    const polygon = pitch.map_polygon;
    if (!polygon || polygon.length < 3) continue;
    doc.shapes.push(
      createShape(
        polygon.map((p) => ({ x: p.x * width, y: p.y * height })),
        { label: pitch.code, layer: "pitch", pitch_id: pitch.id },
      ),
    );
  }
  return doc;
}

// --- Geometrie -------------------------------------------------------------------

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function pointsBounds(points: PlanPoint[]): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

export function shapeCentroid(shape: PlanShape): PlanPoint {
  return polygonCentroid(shape.points as MapPoint[]);
}

export function polygonArea(points: PlanPoint[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    area += a.x * b.y - b.x * a.y;
  }
  return Math.abs(area) / 2;
}

export function pointInPolygon(point: PlanPoint, polygon: PlanPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const pi = polygon[i]!;
    const pj = polygon[j]!;
    const crosses =
      pi.y > point.y !== pj.y > point.y &&
      point.x < ((pj.x - pi.x) * (point.y - pi.y)) / (pj.y - pi.y) + pi.x;
    if (crosses) inside = !inside;
  }
  return inside;
}

export function translatePoints(points: PlanPoint[], dx: number, dy: number): PlanPoint[] {
  return points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
}

export function clampPoint(point: PlanPoint, doc: { width: number; height: number }): PlanPoint {
  return {
    x: Math.min(doc.width, Math.max(0, point.x)),
    y: Math.min(doc.height, Math.max(0, point.y)),
  };
}

export function roundPoint(point: PlanPoint, decimals = 2): PlanPoint {
  const f = 10 ** decimals;
  return { x: Math.round(point.x * f) / f, y: Math.round(point.y * f) / f };
}

export function pointsToAttr(points: PlanPoint[]): string {
  return points.map((p) => `${round2(p.x)},${round2(p.y)}`).join(" ");
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Standard-Symbolgröße relativ zur Plangröße (gut lesbar, nicht dominant). */
export function defaultSymbolSize(doc: { width: number; height: number }): number {
  return Math.max(8, Math.round(Math.min(doc.width, doc.height) * 0.035));
}

export function defaultTextSize(doc: { width: number; height: number }): number {
  return Math.max(8, Math.round(Math.min(doc.width, doc.height) * 0.03));
}

/** Schriftgröße, die in der Fläche bleibt. Fette Glyphen sind breiter als 0.6em. */
export function shapeLabelFontSize(shape: PlanShape, label: string): number {
  const b = pointsBounds(shape.points);
  const w = Math.max(1, b.maxX - b.minX);
  const h = Math.max(1, b.maxY - b.minY);
  const chars = Math.max(1, label.length);
  const byHeight = h * 0.55;
  const byWidth = (w * 0.86) / (chars * 0.68);
  return Math.max(2, Math.min(byHeight, byWidth));
}

/** Breite, auf die die Beschriftung gestaucht wird, wenn sie sonst aus der Fläche läuft. */
export function shapeLabelTextLength(shape: PlanShape, label: string, fontSize: number): number | null {
  const b = pointsBounds(shape.points);
  const available = Math.max(4, (b.maxX - b.minX) * 0.9);
  const estimated = fontSize * 0.68 * Math.max(1, label.length);
  return estimated > available ? available : null;
}

export function labelKey(value: string): string {
  return value.replace(/\s+/g, "").toUpperCase();
}

/** Text liegt in der Fläche und wiederholt deren Nummer oder den Platzcode. */
export function textDuplicatesShapeLabel(
  text: PlanText,
  shape: PlanShape,
  pitchCode: string | null,
): boolean {
  if (!pointInPolygon({ x: text.x, y: text.y }, shape.points)) return false;
  const key = labelKey(text.text);
  if (!key) return false;
  if (shape.label && labelKey(shape.label) === key) return true;
  return Boolean(pitchCode && labelKey(pitchCode) === key);
}

export function isDuplicateLabelText(
  text: PlanText,
  doc: SitePlanVectorDocument,
  pitchCodes: ReadonlyMap<string, string>,
): boolean {
  return doc.shapes.some((shape) =>
    textDuplicatesShapeLabel(text, shape, shape.pitch_id ? (pitchCodes.get(shape.pitch_id) ?? null) : null),
  );
}

/** Füllfarbe: zugeordnete Plätze übernehmen die Kategoriefarbe, bis eine eigene gesetzt ist. */
export function resolvedShapeFill(
  shape: PlanShape,
  categoryColors?: ReadonlyMap<string, string | null>,
): string | null {
  if (shape.use_category_color !== false && shape.pitch_id && categoryColors) {
    const color = categoryColors.get(shape.pitch_id)?.trim();
    if (color) return color;
  }
  return shape.fill;
}

// --- Dateien ---------------------------------------------------------------------

export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Datei konnte nicht gelesen werden."));
    };
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden."));
    reader.readAsText(file);
  });
}

export function loadImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
      } else {
        reject(new Error("Bildgröße konnte nicht ermittelt werden."));
      }
    };
    image.onerror = () => reject(new Error("Bild konnte nicht geladen werden."));
    image.src = dataUrl;
  });
}

/**
 * Planbild für die KI vorbereiten: auf max. `maxSide` Pixel verkleinern und als PNG
 * kodieren. Liefert Data-URL und die tatsächlichen Pixelmaße (Koordinatenrahmen).
 */
export async function prepareImageForVectorize(
  file: File,
  maxSide = 2048,
): Promise<{ dataUrl: string; width: number; height: number }> {
  const bitmap = await createImageBitmap(file).catch(() => {
    throw new Error("Das Bildformat wird nicht unterstützt. Bitte PNG, JPEG oder WebP verwenden.");
  });
  try {
    const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Bildverarbeitung nicht möglich.");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(bitmap, 0, 0, width, height);
    const dataUrl = canvas.toDataURL("image/png");
    return { dataUrl, width, height };
  } finally {
    bitmap.close();
  }
}

export function downloadTextFile(filename: string, text: string, mime = "image/svg+xml"): void {
  const blob = new Blob([text], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// --- SVG-Export ------------------------------------------------------------------

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export interface ExportSvgOptions {
  /** Quellbild (Data-URL) als unterste Ebene einbetten. */
  sourceImage?: string | null;
  /** Platzcodes/Labels auf die Flächen schreiben. */
  includeLabels?: boolean;
  /** Platzcode je Stellplatz-ID (für zugeordnete Flächen). */
  pitchCodes?: ReadonlyMap<string, string>;
  /** Farbe der Platzkategorie je Stellplatz-ID. */
  pitchCategoryColors?: ReadonlyMap<string, string | null>;
  /**
   * Darstellung zugeordneter Platzflächen:
   * `normal` = wie gezeichnet; `neutral` = helle Fläche mit Nummer (Hintergrund für den
   * Live-Platzplan, der die Belegungsfarbe selbst darüberlegt); `omit` = weglassen.
   */
  assignedPitchStyle?: "normal" | "neutral" | "omit";
  /**
   * Editor-Dokument als `<metadata>` einbetten (für verlustfreien Re-Import).
   * Der veröffentlichte Live-Hintergrund braucht das nicht – die Metadaten
   * vergrößern das SVG und verlangsamen das Zeichnen.
   */
  embedDocument?: boolean;
}

const NEUTRAL_PITCH_FILL = "#f3f4f6";
const EMBEDDED_DOCUMENT_FORMAT = "campdesk-site-plan/1";

export function shapeDisplayLabel(shape: PlanShape, pitchCodes?: ReadonlyMap<string, string>): string | null {
  if (shape.show_label === false) return null;
  if (shape.label) return shape.label;
  if (shape.pitch_id && pitchCodes?.has(shape.pitch_id)) return pitchCodes.get(shape.pitch_id)!;
  return null;
}

export function exportDocumentToSvg(doc: SitePlanVectorDocument, options: ExportSvgOptions = {}): string {
  const includeLabels = options.includeLabels ?? true;
  const parts: string[] = [];
  const w = round2(doc.width);
  const h = round2(doc.height);
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" data-campdesk-site-plan="1">`,
  );
  // Das vollständige Dokument wird eingebettet, damit ein Re-Import verlustfrei ist
  // (Platz-Zuordnungen, Symbole, Ebenen). Der Live-Hintergrund lässt es weg.
  if (options.embedDocument !== false) {
    parts.push(
      `<metadata data-format="${EMBEDDED_DOCUMENT_FORMAT}">${escapeXml(JSON.stringify(doc))}</metadata>`,
    );
  }
  parts.push(serializeSymbolDefs(doc.symbols.map((s) => s.type)));
  parts.push(
    `<rect x="0" y="0" width="${w}" height="${h}" fill="${escapeXml(doc.background_color ?? "#ffffff")}" data-role="background"/>`,
  );
  if (options.sourceImage) {
    parts.push(
      `<image href="${escapeXml(options.sourceImage)}" x="0" y="0" width="${w}" height="${h}" preserveAspectRatio="none"/>`,
    );
  }

  const assignedStyle = options.assignedPitchStyle ?? "normal";
  parts.push(`<g data-layer="shapes">`);
  const sorted = [...doc.shapes].sort((a, b) => layerOrder(a.layer) - layerOrder(b.layer));
  for (const shape of sorted) {
    const assigned = Boolean(shape.pitch_id);
    if (assigned && assignedStyle === "omit") continue;
    const ownFill = resolvedShapeFill(shape, options.pitchCategoryColors) ?? layerDefaultFill(shape.layer);
    const fill = assigned && assignedStyle === "neutral" ? NEUTRAL_PITCH_FILL : ownFill;
    const attrs = [
      `points="${pointsToAttr(shape.points)}"`,
      `fill="${escapeXml(fill)}"`,
      `stroke="${escapeXml(shape.stroke ?? "#1f2937")}"`,
      `stroke-width="${shape.stroke_width ?? Math.max(0.5, Math.min(w, h) * 0.0015)}"`,
      `stroke-linejoin="round"`,
      `data-id="${escapeXml(shape.id)}"`,
      `data-layer="${shape.layer}"`,
    ];
    if (shape.opacity !== null && shape.opacity < 1) attrs.push(`fill-opacity="${shape.opacity}"`);
    if (shape.pitch_id) attrs.push(`data-pitch-id="${escapeXml(shape.pitch_id)}"`);
    const title = shapeDisplayLabel(shape, options.pitchCodes);
    parts.push(`<polygon ${attrs.join(" ")}>${title ? `<title>${escapeXml(title)}</title>` : ""}</polygon>`);
  }
  parts.push(`</g>`);

  if (includeLabels) {
    parts.push(`<defs>`);
    for (const shape of sorted) {
      parts.push(`<clipPath id="sp-label-${escapeXml(shape.id)}"><polygon points="${pointsToAttr(shape.points)}"/></clipPath>`);
    }
    parts.push(`</defs>`);
    parts.push(`<g data-layer="labels" font-family="system-ui, -apple-system, 'Segoe UI', sans-serif" font-weight="600">`);
    for (const shape of sorted) {
      if (shape.pitch_id && assignedStyle === "omit") continue;
      const label = shapeDisplayLabel(shape, options.pitchCodes);
      if (!label) continue;
      const c = shapeCentroid(shape);
      const size = shapeLabelFontSize(shape, label);
      const textLength = shapeLabelTextLength(shape, label, size);
      const lengthAttr = textLength !== null ? ` textLength="${round2(textLength)}" lengthAdjust="spacingAndGlyphs"` : "";
      parts.push(
        `<text x="${round2(c.x)}" y="${round2(c.y)}" font-size="${round2(size)}" text-anchor="middle" dominant-baseline="central" fill="#111827" stroke="#ffffff" stroke-width="${round2(size * 0.12)}" paint-order="stroke" stroke-linejoin="round" clip-path="url(#sp-label-${escapeXml(shape.id)})"${lengthAttr}>${escapeXml(label)}</text>`,
      );
    }
    parts.push(`</g>`);
  }

  parts.push(`<g data-layer="symbols">`);
  for (const symbol of doc.symbols) {
    const def = getSymbolDefinition(symbol.type);
    const scale = symbol.size / SYMBOL_BOX;
    const transform = `translate(${round2(symbol.x)} ${round2(symbol.y)}) rotate(${round2(symbol.rotation)}) scale(${round2(scale)}) translate(${-SYMBOL_BOX / 2} ${-SYMBOL_BOX / 2})`;
    parts.push(
      `<use href="#${symbolDomId(symbol.type)}" width="${SYMBOL_BOX}" height="${SYMBOL_BOX}" transform="${transform}" color="${escapeXml(symbol.color ?? def.color)}" data-id="${escapeXml(symbol.id)}" data-type="${escapeXml(def.type)}">${symbol.label ? `<title>${escapeXml(symbol.label)}</title>` : ""}</use>`,
    );
  }
  parts.push(`</g>`);

  parts.push(`<g data-layer="texts" font-family="system-ui, -apple-system, 'Segoe UI', sans-serif" font-weight="600">`);
  for (const text of doc.texts) {
    if (options.pitchCodes && isDuplicateLabelText(text, doc, options.pitchCodes)) continue;
    const transform = text.rotation ? ` transform="rotate(${round2(text.rotation)} ${round2(text.x)} ${round2(text.y)})"` : "";
    parts.push(
      `<text x="${round2(text.x)}" y="${round2(text.y)}" font-size="${round2(text.size)}" text-anchor="middle" dominant-baseline="central" fill="${escapeXml(text.color ?? "#111827")}" data-id="${escapeXml(text.id)}"${transform}>${escapeXml(text.text)}</text>`,
    );
  }
  parts.push(`</g>`);
  parts.push(`</svg>`);
  return parts.join("\n");
}

const LAYER_ORDER: Record<PlanLayer, number> = {
  green: 0,
  water: 1,
  parking: 2,
  road: 3,
  other: 4,
  building: 5,
  pitch: 6,
};

export function layerOrder(layer: PlanLayer): number {
  return LAYER_ORDER[layer] ?? 4;
}

export function svgToDataUrl(svg: string): string {
  const bytes = new TextEncoder().encode(svg);
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return `data:image/svg+xml;base64,${btoa(binary)}`;
}

// --- SVG-Import ------------------------------------------------------------------

type Matrix = [number, number, number, number, number, number]; // a b c d e f

const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

function multiply(m: Matrix, n: Matrix): Matrix {
  return [
    m[0] * n[0] + m[2] * n[1],
    m[1] * n[0] + m[3] * n[1],
    m[0] * n[2] + m[2] * n[3],
    m[1] * n[2] + m[3] * n[3],
    m[0] * n[4] + m[2] * n[5] + m[4],
    m[1] * n[4] + m[3] * n[5] + m[5],
  ];
}

function applyMatrix(m: Matrix, p: PlanPoint): PlanPoint {
  return { x: m[0] * p.x + m[2] * p.y + m[4], y: m[1] * p.x + m[3] * p.y + m[5] };
}

function matrixScale(m: Matrix): number {
  return Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) || 1;
}

function parseTransform(value: string | null): Matrix {
  if (!value) return IDENTITY;
  let result: Matrix = IDENTITY;
  const re = /(matrix|translate|scale|rotate|skewX|skewY)\s*\(([^)]*)\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(value)) !== null) {
    const name = match[1]!;
    const args = match[2]!
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    let m: Matrix = IDENTITY;
    switch (name) {
      case "matrix":
        if (args.length === 6) m = args as Matrix;
        break;
      case "translate":
        m = [1, 0, 0, 1, args[0] ?? 0, args[1] ?? 0];
        break;
      case "scale":
        m = [args[0] ?? 1, 0, 0, args[1] ?? args[0] ?? 1, 0, 0];
        break;
      case "rotate": {
        const a = ((args[0] ?? 0) * Math.PI) / 180;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        const rot: Matrix = [cos, sin, -sin, cos, 0, 0];
        if (args.length >= 3) {
          const cx = args[1]!;
          const cy = args[2]!;
          m = multiply(multiply([1, 0, 0, 1, cx, cy], rot), [1, 0, 0, 1, -cx, -cy]);
        } else {
          m = rot;
        }
        break;
      }
      case "skewX":
        m = [1, 0, Math.tan(((args[0] ?? 0) * Math.PI) / 180), 1, 0, 0];
        break;
      case "skewY":
        m = [1, Math.tan(((args[0] ?? 0) * Math.PI) / 180), 0, 1, 0, 0];
        break;
    }
    result = multiply(result, m);
  }
  return result;
}

function parseLength(value: string | null): number | null {
  if (!value) return null;
  const n = Number.parseFloat(value.replace(/[a-z%]+$/i, ""));
  return Number.isFinite(n) ? n : null;
}

const SAFE_COLOR_RE =
  /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]{1,30}|rgba?\([\d\s.,%]+\)|hsla?\([\d\s.,%]+\))$/;

function styleProp(el: Element, name: string): string | null {
  const style = el.getAttribute("style");
  if (style) {
    const re = new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, "i");
    const m = style.match(re);
    if (m?.[1]) return m[1].trim();
  }
  return el.getAttribute(name);
}

interface InheritedStyle {
  fill: string | null;
  stroke: string | null;
  strokeWidth: number | null;
  opacity: number | null;
  layerHint: PlanLayer | null;
}

function normalizeColor(value: string | null): string | null | "none" {
  if (!value) return null;
  const v = value.trim();
  if (!v || v === "inherit" || v === "currentColor") return null;
  if (v === "none" || v === "transparent") return "none";
  return SAFE_COLOR_RE.test(v) ? v : null;
}

function inheritStyle(el: Element, parent: InheritedStyle): InheritedStyle {
  const fill = normalizeColor(styleProp(el, "fill"));
  const stroke = normalizeColor(styleProp(el, "stroke"));
  const strokeWidth = parseLength(styleProp(el, "stroke-width"));
  const opacityRaw = parseLength(styleProp(el, "fill-opacity")) ?? parseLength(styleProp(el, "opacity"));
  const hint = guessLayerFromName(
    [el.getAttribute("id"), el.getAttribute("class"), el.getAttribute("data-layer"), el.getAttribute("inkscape:label")]
      .filter(Boolean)
      .join(" "),
  );
  return {
    fill: fill === null ? parent.fill : fill,
    stroke: stroke === null ? parent.stroke : stroke,
    strokeWidth: strokeWidth ?? parent.strokeWidth,
    opacity: opacityRaw ?? parent.opacity,
    layerHint: hint ?? parent.layerHint,
  };
}

const LAYER_NAME_HINTS: [RegExp, PlanLayer][] = [
  [/pitch|stellpl|platz|parzell|site|lot/i, "pitch"],
  [/road|weg|stra(ss|ß)e|street|path|fahr/i, "road"],
  [/build|geb(ae|ä)ude|haus|sanit/i, "building"],
  [/water|wasser|fluss|river|see|lake|main/i, "water"],
  [/green|gr(ue|ü)n|wiese|rasen|grass|wald|tree/i, "green"],
  [/parking|parkpl/i, "parking"],
];

function guessLayerFromName(name: string): PlanLayer | null {
  if (!name) return null;
  for (const [re, layer] of LAYER_NAME_HINTS) {
    if (re.test(name)) return layer;
  }
  return null;
}

/** Farbton-Heuristik für Flächen ohne Benennung: Blau → Wasser, Grün → Grün, Grau → Weg. */
function guessLayerFromColor(fill: string | null): PlanLayer {
  const rgb = parseColorToRgb(fill);
  if (!rgb) return "other";
  const [r, g, b] = rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const sat = max === 0 ? 0 : (max - min) / max;
  if (sat < 0.12) return max > 240 ? "other" : "road";
  if (b > r && b > g) return "water";
  if (g > r && g > b) return "green";
  return "other";
}

function parseColorToRgb(value: string | null): [number, number, number] | null {
  if (!value) return null;
  const v = value.trim();
  const hex = v.match(/^#([0-9a-f]{3,8})$/i)?.[1];
  if (hex) {
    if (hex.length === 3 || hex.length === 4) {
      return [
        parseInt(hex[0]! + hex[0]!, 16),
        parseInt(hex[1]! + hex[1]!, 16),
        parseInt(hex[2]! + hex[2]!, 16),
      ];
    }
    if (hex.length >= 6) {
      return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
    }
  }
  const rgb = v.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  const named: Record<string, [number, number, number]> = {
    blue: [0, 0, 255],
    green: [0, 128, 0],
    red: [255, 0, 0],
    gray: [128, 128, 128],
    grey: [128, 128, 128],
    white: [255, 255, 255],
    black: [0, 0, 0],
    orange: [255, 165, 0],
    yellow: [255, 255, 0],
    lightblue: [173, 216, 230],
    lightgreen: [144, 238, 144],
  };
  return named[v.toLowerCase()] ?? null;
}

/** Pfad-`d` in Teilpfade (Punktlisten) zerlegen; Kurven werden abgetastet. */
function flattenPathData(d: string): PlanPoint[][] {
  const tokens = d.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?/g);
  if (!tokens) return [];
  const subpaths: PlanPoint[][] = [];
  let current: PlanPoint[] = [];
  let cmd = "";
  let i = 0;
  let cx = 0;
  let cy = 0;
  let startX = 0;
  let startY = 0;
  let lastCtrlX = 0;
  let lastCtrlY = 0;
  let lastCmd = "";

  const num = (): number => {
    const t = tokens[i++];
    return t === undefined ? 0 : Number(t);
  };
  const push = (x: number, y: number) => {
    current.push({ x, y });
    cx = x;
    cy = y;
  };
  const closeSubpath = () => {
    if (current.length >= 2) subpaths.push(current);
    current = [];
  };
  const sampleCubic = (x1: number, y1: number, x2: number, y2: number, x: number, y: number) => {
    const x0 = cx;
    const y0 = cy;
    for (let s = 1; s <= 8; s += 1) {
      const t = s / 8;
      const mt = 1 - t;
      const px = mt * mt * mt * x0 + 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t * x;
      const py = mt * mt * mt * y0 + 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t * y;
      current.push({ x: px, y: py });
    }
    cx = x;
    cy = y;
    lastCtrlX = x2;
    lastCtrlY = y2;
  };
  const sampleQuad = (x1: number, y1: number, x: number, y: number) => {
    const x0 = cx;
    const y0 = cy;
    for (let s = 1; s <= 6; s += 1) {
      const t = s / 6;
      const mt = 1 - t;
      current.push({
        x: mt * mt * x0 + 2 * mt * t * x1 + t * t * x,
        y: mt * mt * y0 + 2 * mt * t * y1 + t * t * y,
      });
    }
    cx = x;
    cy = y;
    lastCtrlX = x1;
    lastCtrlY = y1;
  };

  while (i < tokens.length) {
    const token = tokens[i]!;
    if (/^[a-zA-Z]$/.test(token)) {
      cmd = token;
      i += 1;
    } else if (!cmd) {
      i += 1;
      continue;
    }
    const rel = cmd === cmd.toLowerCase();
    const upper = cmd.toUpperCase();
    switch (upper) {
      case "M": {
        if (current.length) closeSubpath();
        const x = num();
        const y = num();
        const nx = rel ? cx + x : x;
        const ny = rel ? cy + y : y;
        push(nx, ny);
        startX = nx;
        startY = ny;
        // Folgende Koordinatenpaare gelten als L
        cmd = rel ? "l" : "L";
        break;
      }
      case "L": {
        const x = num();
        const y = num();
        push(rel ? cx + x : x, rel ? cy + y : y);
        break;
      }
      case "H": {
        const x = num();
        push(rel ? cx + x : x, cy);
        break;
      }
      case "V": {
        const y = num();
        push(cx, rel ? cy + y : y);
        break;
      }
      case "C": {
        const x1 = num();
        const y1 = num();
        const x2 = num();
        const y2 = num();
        const x = num();
        const y = num();
        const ox = rel ? cx : 0;
        const oy = rel ? cy : 0;
        sampleCubic(ox + x1, oy + y1, ox + x2, oy + y2, ox + x, oy + y);
        break;
      }
      case "S": {
        const x2 = num();
        const y2 = num();
        const x = num();
        const y = num();
        const ox = rel ? cx : 0;
        const oy = rel ? cy : 0;
        const reflectX = /^[CcSs]$/.test(lastCmd) ? 2 * cx - lastCtrlX : cx;
        const reflectY = /^[CcSs]$/.test(lastCmd) ? 2 * cy - lastCtrlY : cy;
        sampleCubic(reflectX, reflectY, ox + x2, oy + y2, ox + x, oy + y);
        break;
      }
      case "Q": {
        const x1 = num();
        const y1 = num();
        const x = num();
        const y = num();
        const ox = rel ? cx : 0;
        const oy = rel ? cy : 0;
        sampleQuad(ox + x1, oy + y1, ox + x, oy + y);
        break;
      }
      case "T": {
        const x = num();
        const y = num();
        const ox = rel ? cx : 0;
        const oy = rel ? cy : 0;
        const reflectX = /^[QqTt]$/.test(lastCmd) ? 2 * cx - lastCtrlX : cx;
        const reflectY = /^[QqTt]$/.test(lastCmd) ? 2 * cy - lastCtrlY : cy;
        sampleQuad(reflectX, reflectY, ox + x, oy + y);
        break;
      }
      case "A": {
        // Bogen: rx ry rot large sweep x y – vereinfacht auf Endpunkt mit Zwischenpunkt
        num();
        num();
        num();
        num();
        num();
        const x = num();
        const y = num();
        const nx = rel ? cx + x : x;
        const ny = rel ? cy + y : y;
        current.push({ x: (cx + nx) / 2, y: (cy + ny) / 2 });
        push(nx, ny);
        break;
      }
      case "Z": {
        closeSubpath();
        cx = startX;
        cy = startY;
        break;
      }
      default:
        i += 1;
    }
    lastCmd = cmd;
  }
  if (current.length) closeSubpath();
  return subpaths;
}

function dedupePoints(points: PlanPoint[]): PlanPoint[] {
  const out: PlanPoint[] = [];
  for (const p of points) {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - p.x) < 1e-6 && Math.abs(last.y - p.y) < 1e-6) continue;
    out.push(p);
  }
  if (out.length > 1) {
    const first = out[0]!;
    const last = out[out.length - 1]!;
    if (Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6) out.pop();
  }
  return out;
}

/** Zu viele Stützpunkte (abgetastete Kurven) auf eine handhabbare Zahl reduzieren. */
function simplifyPoints(points: PlanPoint[], tolerance: number): PlanPoint[] {
  if (points.length <= 8 || tolerance <= 0) return points;
  const result: PlanPoint[] = [points[0]!];
  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = result[result.length - 1]!;
    const curr = points[i]!;
    const next = points[i + 1]!;
    const dx = next.x - prev.x;
    const dy = next.y - prev.y;
    const len = Math.hypot(dx, dy) || 1;
    const dist = Math.abs(dy * curr.x - dx * curr.y + next.x * prev.y - next.y * prev.x) / len;
    if (dist > tolerance) result.push(curr);
  }
  result.push(points[points.length - 1]!);
  return result;
}

const SKIP_TAGS = new Set([
  "defs",
  "clippath",
  "mask",
  "symbol",
  "marker",
  "pattern",
  "style",
  "title",
  "desc",
  "metadata",
  "lineargradient",
  "radialgradient",
  "filter",
  "script",
]);

export interface SvgImportResult {
  document: SitePlanVectorDocument;
  warnings: string[];
}

const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function optionalString(value: unknown, maxLength: number): string | null {
  return typeof value === "string" && value.length > 0 ? value.slice(0, maxLength) : null;
}

function optionalColor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const c = normalizeColor(value);
  return c && c !== "none" ? c : null;
}

/**
 * Eingebettetes Editor-Dokument (`<metadata data-format="campdesk-site-plan/1">`) lesen.
 * Liefert `null`, wenn keins vorhanden oder nicht plausibel ist – dann greift der
 * geometrische Import. Jedes Element wird einzeln geprüft, damit manipulierte
 * Dateien höchstens Elemente verlieren, aber nie kaputte Dokumente erzeugen.
 */
function readEmbeddedDocument(xml: Document): SitePlanVectorDocument | null {
  const node = Array.from(xml.getElementsByTagName("metadata")).find(
    (el) => el.getAttribute("data-format") === EMBEDDED_DOCUMENT_FORMAT,
  );
  if (!node) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(node.textContent ?? "");
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const width = finiteNumber(r.width);
  const height = finiteNumber(r.height);
  if (!width || !height || width <= 0 || height <= 0 || width > 20000 || height > 20000) return null;

  const doc = emptyDocument(width, height);
  doc.background_color = optionalColor(r.background_color);
  const ids = new Set<string>();
  const takeId = (value: unknown, prefix: string): string => {
    let id = typeof value === "string" && ID_RE.test(value) && !ids.has(value) ? value : newElementId(prefix);
    while (ids.has(id)) id = newElementId(prefix);
    ids.add(id);
    return id;
  };
  const layers = new Set<string>(PLAN_LAYERS.map((l) => l.key));
  const usedPitches = new Set<string>();

  for (const item of Array.isArray(r.shapes) ? r.shapes.slice(0, MAX_PLAN_SHAPES) : []) {
    if (!item || typeof item !== "object") continue;
    const s = item as Record<string, unknown>;
    const points: PlanPoint[] = [];
    for (const p of Array.isArray(s.points) ? s.points : []) {
      const x = finiteNumber((p as Record<string, unknown>)?.x);
      const y = finiteNumber((p as Record<string, unknown>)?.y);
      if (x !== null && y !== null) points.push(roundPoint(clampPoint({ x, y }, doc)));
    }
    if (points.length < 3 || points.length > 500) continue;
    const layer = typeof s.layer === "string" && layers.has(s.layer) ? (s.layer as PlanLayer) : "other";
    const pitchId = typeof s.pitch_id === "string" && UUID_RE.test(s.pitch_id) && !usedPitches.has(s.pitch_id) ? s.pitch_id : null;
    if (pitchId) usedPitches.add(pitchId);
    const strokeWidth = finiteNumber(s.stroke_width);
    const opacity = finiteNumber(s.opacity);
    doc.shapes.push({
      id: takeId(s.id, "a"),
      kind: "polygon",
      points,
      label: optionalString(s.label, 80),
      layer,
      fill: s.fill === "none" ? "none" : optionalColor(s.fill),
      stroke: optionalColor(s.stroke),
      stroke_width: strokeWidth !== null ? Math.max(0, Math.min(50, strokeWidth)) : null,
      opacity: opacity !== null ? Math.max(0, Math.min(1, opacity)) : null,
      pitch_id: pitchId,
      locked: s.locked === true,
      show_label: s.show_label === false ? false : undefined,
      use_category_color: s.use_category_color === false ? false : undefined,
    });
  }

  for (const item of Array.isArray(r.symbols) ? r.symbols.slice(0, MAX_PLAN_SHAPES) : []) {
    if (!item || typeof item !== "object") continue;
    const s = item as Record<string, unknown>;
    const x = finiteNumber(s.x);
    const y = finiteNumber(s.y);
    const size = finiteNumber(s.size);
    if (x === null || y === null || size === null || size <= 0) continue;
    const type = typeof s.type === "string" ? getSymbolDefinition(s.type).type : "marker";
    const rotation = finiteNumber(s.rotation) ?? 0;
    doc.symbols.push({
      id: takeId(s.id, "s"),
      type,
      x: round2(Math.min(width, Math.max(0, x))),
      y: round2(Math.min(height, Math.max(0, y))),
      size: round2(Math.min(2000, size)),
      rotation: Math.max(-360, Math.min(360, rotation)),
      label: optionalString(s.label, 80),
      color: optionalColor(s.color),
    });
  }

  for (const item of Array.isArray(r.texts) ? r.texts.slice(0, MAX_PLAN_SHAPES) : []) {
    if (!item || typeof item !== "object") continue;
    const t = item as Record<string, unknown>;
    const x = finiteNumber(t.x);
    const y = finiteNumber(t.y);
    const size = finiteNumber(t.size);
    const text = optionalString(t.text, 120);
    if (x === null || y === null || size === null || size <= 0 || !text) continue;
    const rotation = finiteNumber(t.rotation) ?? 0;
    doc.texts.push({
      id: takeId(t.id, "t"),
      x: round2(Math.min(width, Math.max(0, x))),
      y: round2(Math.min(height, Math.max(0, y))),
      text,
      size: round2(Math.min(500, size)),
      rotation: Math.max(-360, Math.min(360, rotation)),
      color: optionalColor(t.color),
    });
  }

  for (const item of Array.isArray(r.routes) ? r.routes.slice(0, MAX_PLAN_SHAPES) : []) {
    if (!item || typeof item !== "object") continue;
    const route = item as Record<string, unknown>;
    const points: PlanPoint[] = [];
    for (const p of Array.isArray(route.points) ? route.points : []) {
      const x = finiteNumber((p as Record<string, unknown>)?.x);
      const y = finiteNumber((p as Record<string, unknown>)?.y);
      if (x !== null && y !== null) points.push(roundPoint(clampPoint({ x, y }, doc)));
    }
    if (points.length < 2 || points.length > 500) continue;
    const pitchId =
      typeof route.pitch_id === "string" && UUID_RE.test(route.pitch_id) ? route.pitch_id : null;
    const strokeWidth = finiteNumber(route.stroke_width);
    doc.routes.push({
      id: takeId(route.id, "r"),
      points,
      pitch_id: pitchId,
      color: optionalColor(route.color),
      stroke_width: strokeWidth !== null ? Math.max(0, Math.min(50, strokeWidth)) : null,
    });
  }

  return doc;
}

/**
 * SVG-Datei in ein editierbares Dokument wandeln.
 *
 * Unterstützt polygon/polyline/rect/path/circle/ellipse/text inkl. verschachtelter
 * Transformationen. Kurven werden abgetastet, Bögen vereinfacht. Kleine Kreise
 * werden zu Baum-Symbolen, Texte innerhalb einer Fläche zu deren Beschriftung.
 */
export function importSvgDocument(svgText: string): SvgImportResult {
  const warnings: string[] = [];
  const parser = new DOMParser();
  const xml = parser.parseFromString(svgText, "image/svg+xml");
  if (xml.getElementsByTagName("parsererror").length > 0) {
    throw new Error("Die SVG-Datei konnte nicht gelesen werden (ungültiges XML).");
  }
  const root = xml.documentElement;
  if (root.localName.toLowerCase() !== "svg") {
    throw new Error("Die Datei ist kein SVG.");
  }

  // Vom Editor exportiertes SVG: eingebettetes Dokument verlustfrei übernehmen.
  const embedded = readEmbeddedDocument(xml);
  if (embedded) return { document: embedded, warnings: [] };

  let offsetX = 0;
  let offsetY = 0;
  let width = parseLength(root.getAttribute("width"));
  let height = parseLength(root.getAttribute("height"));
  const viewBox = root
    .getAttribute("viewBox")
    ?.split(/[\s,]+/)
    .map(Number)
    .filter((n) => Number.isFinite(n));
  if (viewBox && viewBox.length === 4 && viewBox[2]! > 0 && viewBox[3]! > 0) {
    offsetX = viewBox[0]!;
    offsetY = viewBox[1]!;
    width = viewBox[2]!;
    height = viewBox[3]!;
  }
  if (!width || !height || width <= 0 || height <= 0) {
    width = 1000;
    height = 700;
    warnings.push("SVG ohne Größenangabe – Plan auf 1000 × 700 Einheiten gesetzt.");
  }

  const doc = emptyDocument(width, height);
  const rootMatrix: Matrix = [1, 0, 0, 1, -offsetX, -offsetY];
  const baseStyle: InheritedStyle = { fill: null, stroke: null, strokeWidth: null, opacity: null, layerHint: null };
  const usedIds = new Set<string>();
  const texts: { point: PlanPoint; text: string; size: number; color: string | null }[] = [];
  let skippedUse = 0;
  let skippedImage = 0;
  let skippedTiny = 0;
  const minSide = Math.min(width, height);
  const tinyArea = (minSide * 0.002) ** 2;
  const treeRadiusMax = minSide * 0.03;

  const pickId = (el: Element, prefix: string): string => {
    const raw = el.getAttribute("id");
    if (raw && /^[A-Za-z0-9_-]{1,64}$/.test(raw) && !usedIds.has(raw)) {
      usedIds.add(raw);
      return raw;
    }
    let id = newElementId(prefix);
    while (usedIds.has(id)) id = newElementId(prefix);
    usedIds.add(id);
    return id;
  };

  const canvasArea = width * height;
  let backgroundSeen = false;
  /** Flächen, deren Ebene explizit aus id/class/data-layer stammt (nicht aus der Farbe geraten). */
  const explicitLayer = new Set<string>();

  const addShape = (el: Element, rawPoints: PlanPoint[], m: Matrix, style: InheritedStyle) => {
    if (doc.shapes.length >= MAX_PLAN_SHAPES) return;
    let points = dedupePoints(rawPoints.map((p) => applyMatrix(m, p)));
    points = simplifyPoints(points, minSide * 0.0015);
    if (points.length < 3) return;
    const area = polygonArea(points);
    if (area < tinyArea) {
      skippedTiny += 1;
      return;
    }
    const fill = style.fill === "none" ? null : style.fill;
    // Eine Fläche, die praktisch die ganze Zeichenfläche bedeckt, ist der Hintergrund
    // (z. B. Hintergrund-Rechteck aus Inkscape oder unserem Export) – keine Planfläche.
    if (!backgroundSeen && doc.shapes.length === 0 && area >= canvasArea * 0.97) {
      const b = pointsBounds(points);
      if (b.minX <= width * 0.01 && b.minY <= height * 0.01 && b.maxX >= width * 0.99 && b.maxY >= height * 0.99) {
        backgroundSeen = true;
        if (fill) doc.background_color = fill;
        return;
      }
    }
    const layer: PlanLayer = style.layerHint ?? guessLayerFromColor(fill);
    const scale = matrixScale(m);
    const id = pickId(el, "a");
    if (style.layerHint) explicitLayer.add(id);
    doc.shapes.push({
      id,
      kind: "polygon",
      points: points.map((p) => roundPoint(clampPoint(p, doc))),
      label: null,
      layer,
      fill: fill ?? (style.stroke && style.stroke !== "none" ? "none" : layerDefaultFill(layer)),
      stroke: style.stroke === "none" ? null : style.stroke,
      stroke_width: style.strokeWidth !== null ? round2(style.strokeWidth * scale) : null,
      opacity: style.opacity !== null && style.opacity < 1 ? Math.max(0, Math.min(1, style.opacity)) : null,
      pitch_id: null,
      locked: false,
    });
  };

  const walk = (el: Element, parentMatrix: Matrix, parentStyle: InheritedStyle) => {
    const tag = el.localName.toLowerCase();
    if (SKIP_TAGS.has(tag)) return;
    const m = multiply(parentMatrix, parseTransform(el.getAttribute("transform")));
    const style = inheritStyle(el, parentStyle);

    switch (tag) {
      case "svg":
      case "g":
      case "a":
      case "switch":
        for (const child of Array.from(el.children)) walk(child, m, style);
        return;
      case "polygon":
      case "polyline": {
        const nums = (el.getAttribute("points") ?? "")
          .trim()
          .split(/[\s,]+/)
          .map(Number)
          .filter((n) => Number.isFinite(n));
        const pts: PlanPoint[] = [];
        for (let k = 0; k + 1 < nums.length; k += 2) pts.push({ x: nums[k]!, y: nums[k + 1]! });
        addShape(el, pts, m, style);
        return;
      }
      case "rect": {
        const x = parseLength(el.getAttribute("x")) ?? 0;
        const y = parseLength(el.getAttribute("y")) ?? 0;
        const w = parseLength(el.getAttribute("width")) ?? 0;
        const h = parseLength(el.getAttribute("height")) ?? 0;
        if (w <= 0 || h <= 0) return;
        addShape(
          el,
          [
            { x, y },
            { x: x + w, y },
            { x: x + w, y: y + h },
            { x, y: y + h },
          ],
          m,
          style,
        );
        return;
      }
      case "circle":
      case "ellipse": {
        const cx = parseLength(el.getAttribute("cx")) ?? 0;
        const cy = parseLength(el.getAttribute("cy")) ?? 0;
        const rx = tag === "circle" ? (parseLength(el.getAttribute("r")) ?? 0) : (parseLength(el.getAttribute("rx")) ?? 0);
        const ry = tag === "circle" ? rx : (parseLength(el.getAttribute("ry")) ?? 0);
        if (rx <= 0 || ry <= 0) return;
        const scale = matrixScale(m);
        const fill = style.fill === "none" ? null : style.fill;
        const looksLikeTree = Math.max(rx, ry) * scale <= treeRadiusMax && guessLayerFromColor(fill) === "green";
        if (looksLikeTree && doc.symbols.length < MAX_PLAN_SHAPES) {
          const center = applyMatrix(m, { x: cx, y: cy });
          doc.symbols.push({
            id: pickId(el, "s"),
            type: "tree",
            x: round2(center.x),
            y: round2(center.y),
            size: round2(Math.max(6, Math.max(rx, ry) * scale * 2.4)),
            rotation: 0,
            label: null,
            color: fill,
          });
          return;
        }
        const pts: PlanPoint[] = [];
        const segments = 24;
        for (let k = 0; k < segments; k += 1) {
          const a = (k / segments) * Math.PI * 2;
          pts.push({ x: cx + Math.cos(a) * rx, y: cy + Math.sin(a) * ry });
        }
        addShape(el, pts, m, style);
        return;
      }
      case "path": {
        const d = el.getAttribute("d");
        if (!d) return;
        for (const sub of flattenPathData(d)) addShape(el, sub, m, style);
        return;
      }
      case "text": {
        const content = (el.textContent ?? "").replace(/\s+/g, " ").trim();
        if (!content) return;
        const x = parseLength(el.getAttribute("x")) ?? 0;
        const y = parseLength(el.getAttribute("y")) ?? 0;
        const size = (parseLength(styleProp(el, "font-size")) ?? 16) * matrixScale(m);
        const color = normalizeColor(styleProp(el, "fill"));
        texts.push({
          point: applyMatrix(m, { x, y }),
          text: content.slice(0, 120),
          size: Math.min(500, Math.max(4, size)),
          color: color === "none" ? null : color,
        });
        return;
      }
      case "use":
        skippedUse += 1;
        return;
      case "image":
        skippedImage += 1;
        return;
      default:
        for (const child of Array.from(el.children)) walk(child, m, style);
    }
  };

  walk(root, rootMatrix, baseStyle);

  // Kurze Texte innerhalb einer Fläche werden zur Flächenbeschriftung (Platznummer).
  const sortedByArea = [...doc.shapes].sort((a, b) => polygonArea(a.points) - polygonArea(b.points));
  for (const t of texts) {
    const probe = { x: t.point.x, y: t.point.y - t.size * 0.35 };
    const host =
      t.text.length <= 8
        ? sortedByArea.find((s) => !s.label && (pointInPolygon(t.point, s.points) || pointInPolygon(probe, s.points)))
        : undefined;
    if (host) {
      host.label = t.text;
      if (!explicitLayer.has(host.id) && (host.layer === "other" || host.layer === "road")) host.layer = "pitch";
      continue;
    }
    if (doc.texts.length < MAX_PLAN_SHAPES) {
      doc.texts.push({
        id: newElementId("t"),
        x: round2(Math.min(doc.width, Math.max(0, t.point.x))),
        y: round2(Math.min(doc.height, Math.max(0, t.point.y))),
        text: t.text,
        size: round2(t.size),
        rotation: 0,
        color: t.color,
      });
    }
  }

  if (skippedUse) warnings.push(`${skippedUse} <use>-Verweis(e) übersprungen (Symbole bitte aus der Bibliothek setzen).`);
  if (skippedImage) warnings.push(`${skippedImage} eingebettete(s) Bild(er) ignoriert – Quellbild separat hochladen.`);
  if (skippedTiny) warnings.push(`${skippedTiny} winzige Fläche(n) ignoriert.`);
  if (doc.shapes.length >= MAX_PLAN_SHAPES) warnings.push(`Maximal ${MAX_PLAN_SHAPES} Flächen übernommen.`);
  if (doc.shapes.length === 0 && doc.symbols.length === 0) {
    warnings.push("Keine Flächen erkannt – das SVG enthält evtl. nur Bilder oder nicht unterstützte Elemente.");
  }
  return { document: doc, warnings };
}
